import { Box, Chip, IconButton, Stack, Switch, Typography, useTheme } from '@mui/material';
import { MdOutlineEdit } from 'react-icons/md';
import { AgencyAddon } from '@/apis/agency/addons/types';
import {
  AddonPriceBadgePiece,
  formatCommitmentLabel,
  getAddonDisplayTitle,
  getAddonIcon,
  getAddonPriceBadgePieces,
  getAddonShortBlurb
} from './addonDisplay';

interface AddonCardProps {
  addon: AgencyAddon;
  isToggling?: boolean;
  onToggle: (active: boolean) => void;
  onEdit: () => void;
}

function AddonPriceBadgeLabel({ pieces }: { pieces: AddonPriceBadgePiece[] }) {
  const theme = useTheme();
  const amountColor = theme.palette.primary.dark;
  const unitColor = theme.palette.primary.main;

  return (
    <Box
      component='span'
      sx={{
        display: 'inline',
        fontSize: 12,
        lineHeight: 1.4,
        color: unitColor
      }}
    >
      {pieces.map((piece, index) => {
        if (piece.type === 'dot') {
          return (
            <Box component='span' key={`dot-${index}`} sx={{ color: unitColor, fontWeight: 400 }}>
              {' · '}
            </Box>
          );
        }

        if (piece.type === 'or') {
          return (
            <Box component='span' key={`or-${index}`} sx={{ color: unitColor, fontWeight: 400 }}>
              {' or '}
            </Box>
          );
        }

        return (
          <Box component='span' key={`seg-${index}`}>
            <Box
              component='span'
              sx={{
                color: piece.emphasizeAmount === false ? unitColor : amountColor,
                fontWeight: piece.emphasizeAmount === false ? 400 : 700
              }}
            >
              {piece.amount}
            </Box>
            <Box component='span' sx={{ color: unitColor, fontWeight: 400 }}>
              {piece.unit}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default function AddonCard({ addon, isToggling, onToggle, onEdit }: AddonCardProps) {
  const theme = useTheme();
  const pricePieces = getAddonPriceBadgePieces(addon);

  return (
    <Box
      bgcolor='#fff'
      border='1px solid #ECECEC'
      borderRadius={2}
      p={2.5}
      display='flex'
      flexDirection='column'
      height='100%'
    >
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' gap={1}>
        <Stack direction='row' alignItems='center' gap={1} minWidth={0}>
          <Box
            sx={{
              padding: '7px',
              borderRadius: '50%',
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {getAddonIcon(addon.code)}
          </Box>
          <Typography fontWeight={500} color='text.primary' sx={{ fontSize: 16 }}>
            {getAddonDisplayTitle(addon)}
          </Typography>
        </Stack>

        <Stack direction='row' alignItems='center' gap={0.5} flexShrink={0}>
          <Switch
            checked={!!addon.active}
            onChange={(event) => onToggle(event.target.checked)}
            disabled={isToggling}
            disableRipple
            sx={{
              '& .MuiSwitch-track': {
                height: 14,
                borderRadius: 7,
                backgroundColor: '#E0E0E0',
                opacity: 1
              }
            }}
          />
          <IconButton size='small' onClick={onEdit} aria-label={`Edit ${getAddonDisplayTitle(addon)}`}>
            <MdOutlineEdit size={24} />
          </IconButton>
        </Stack>
      </Stack>

      <Typography mt={1.5} color='text.secondary' fontSize={13} lineHeight={1.5} sx={{ flex: 1 }}>
        {getAddonShortBlurb(addon)}
      </Typography>

      <Stack direction='row' alignItems='center' justifyContent='start' mt={1} flexWrap='wrap' gap={1}>
        <Chip
          label={<AddonPriceBadgeLabel pieces={pricePieces} />}
          size='small'
          sx={{
            bgcolor: theme.palette.primary.light,
            borderRadius: '100px',
            height: 'auto',
            py: 0.75,
            maxWidth: '100%',
            '& .MuiChip-label': {
              whiteSpace: 'normal',
              px: 1.5,
              py: 0.25
            }
          }}
        />
        <Typography
          fontSize={13}
          color='text.secondary'
          whiteSpace='nowrap'
          borderLeft='1px solid #ECECEC'
          paddingLeft={1}
        >
          {formatCommitmentLabel(addon.commitment_months)}
        </Typography>
      </Stack>
    </Box>
  );
}
